<template>
  <div class="px-4 xl:px-24">
    <div class="col-span-1 xl:row-start-1 xl:col-start-2 xl:row-end-1 xl:col-end-5 grid pt-2 pb-2 xl:py-4 bg-white rounded-3xl">
      <div class="col-span-full xl:row-start-1 xl:col-start-1 xl:row-end-1 xl:col-end-5 grid px-12 md:px-36 xl:px-48 py-4 xl:py-0">
        <h3 class="text-center text-khaki-1 mb-2 font-semibold text-app-primary-1-red--fire-brick text-2xl xl:text-3xl">
          {{ submissionState !== 'ok' ? $t('recommendation.suggestion-page.mailblock-title') : $t('recommendation.suggestion-page.mailblock-success-text') }}
          <AtomIconCircledCheck v-if="submissionState === 'ok'" class="inline-flex w-6 ml-3 mb-2" />
          <AtomIconBell v-else class="inline-flex w-6 ml-3 mb-2" />
        </h3>
        <div class="flex flex-col justify-center xl:flex-row gap-6">
          <div class="xl:order-2">
            <div v-if="submissionState !== 'ok'" class="flex flex-col items-center gap-4">
              <p class="text-center text-khaki-1">
                {{ $t('recommendation.suggestion-page.mailblock-text') }}
              </p>
              <input
                ref="emailInputRef"
                type="email"
                :placeholder="$t('recommendation.suggestion-page.mailblock-input-placeholder')"
                class="px-4 py-2 w-full border border-app-primary-1-red--fire-brick border-x-0 border-t-0"
              />
              <MoleculeButton class="mr-2 w-48" :background-color="buttonBackgroundColor" size="large" @click="submitRecommendationEmailRequest">
                {{ $t('recommendation.suggestion-page.mailblock-button-text') }}
              </MoleculeButton>
            </div>
            <div v-else>
              <p class="text-center text-khaki-1">
                {{ $t('recommendation.suggestion-page.mailblock-success-text-subtitle') }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'

const recommendationsStore = useRecommendationsStore()
const bridgeFetch = useBridgeFetch()
const { locale } = useI18n()
const { tixUser } = setupTix()

const recommendationAndScores = computed(() => recommendationsStore.recommendationEvents)
const userScoreId = computed(() => recommendationsStore.userScoreId)
const scoreHash = computed(() => recommendationsStore.scoreHash)

const emailInputRef = ref<HTMLInputElement | null>(null)
const submissionState = ref<'loading' | 'ok' | 'error' | undefined>(undefined)
const buttonBackgroundColor = computed(() => {
  switch (submissionState.value) {
    case 'loading':
      return 'bg-gray-400 pointer-events-none cursor-default'
    case 'ok':
      return 'bg-gray-400 pointer-events-none cursor-default'
    default:
      return 'bg-app-primary-1-red--fire-brick group-hover:bg-app-primary-1-red--fire-brick'
  }
})

async function submitRecommendationEmailRequest() {
  const user = tixUser?.value
  const email = emailInputRef?.value?.value

  if (email) {
    const recommendationsSortedByScore = Object.entries<number>(recommendationAndScores.value.recommended_concerts).sort(([, valueA], [, valueB]) => valueB - valueA)
    const recommendationIds = recommendationsSortedByScore.map(([key]) => Number(key))

    try {
      submissionState.value = 'loading'

      await bridgeFetch(`/clang/${locale.value}/submit-recommendation-email-request`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
        },
        body: JSON.stringify({
          email,
          recommendations: recommendationIds,
          ...(user && { userId: user.id }),
        }),
      })

      submissionState.value = 'ok'
    } catch (e) {
      submissionState.value = 'error'
      console.error('Error submitting recommendation email request', e)
    }
    if (userScoreId.value) {
      try {
        await recommendationsStore.updateUserScores({ userScoreId: userScoreId.value, scoreHash: scoreHash.value, email })
      } catch (e) {
        console.error('Error storing user score', e)
      }
    }
  }
}
</script>

<style scoped lang="scss"></style>
